import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { InfiniteData } from 'react-query';
import { Link } from 'react-scroll';

import { PrimaryButton } from '@components/Button/variants';
import Comment from '@components/Comment';
import Feed from '@components/Feed';
import IconArticles from '@components/Images/Rebranding/IconArticles';
import IconAvatar from '@components/Images/Rebranding/IconAvatar';
import IconComment from '@components/Images/Rebranding/IconComment';
import IconForum from '@components/Images/Rebranding/IconForum';
import IconLike from '@components/Images/Rebranding/IconLike';
import IconOpenTo from '@components/Images/Rebranding/IconOpenTo';
import IconPencil from '@components/Images/Rebranding/IconPencil';
import IconTopic from '@components/Images/Rebranding/IconTopic';
import IconWeekend from '@components/Images/Rebranding/IconWeekend';
import Spinner from '@components/Spinner';
import { ForumPost } from '@utils/backend-api/forumPosts';
import {
  SubscriberCommentsResponse,
  SubscriberProfile,
} from '@utils/backend-api/subscriber_directory';

import ForumPostList from '../Forum/ForumPostList';
import GatedContent from '../Forum/GatedContent';
import OpenToSection from './OpenToSection';
import Section from './Section';

interface ProfileContentProps
  extends Pick<
    SubscriberProfile,
    | 'name'
    | 'bio'
    | 'favoriteBooks'
    | 'favoriteShows'
    | 'favoriteSportsToPlay'
    | 'favoriteSportsToWatch'
    | 'favoriteVacationSpots'
    | 'findingNewJob'
    | 'findingNewJobDesc'
    | 'hiring'
    | 'hiringDesc'
    | 'investingInStartups'
    | 'investingInStartupsDesc'
    | 'isCurrentUserLead'
    | 'isOwnProfile'
    | 'providingServices'
    | 'providingServicesDesc'
    | 'contributorContent'
    | 'contributorContentPagination'
    | 'skills'
    | 'topics'
  > {
  canViewFullProfile: boolean;
  commentsData: InfiniteData<SubscriberCommentsResponse>;
  commentLikesData: InfiniteData<SubscriberCommentsResponse>;
  fetchMoreCommentLikes: () => void;
  fetchMoreComments: () => void;
  forumPosts: ForumPost[];
  hasMoreComments: boolean;
  hasMoreCommentLikes: boolean;
  isFetchingMoreComments: boolean;
  isFetchingMoreCommentLikes: boolean;
  username: string;
}

const ProfileContent = ({
  bio,
  canViewFullProfile,
  commentsData,
  commentLikesData,
  contributorContent,
  contributorContentPagination,
  favoriteBooks,
  favoriteShows,
  favoriteSportsToPlay,
  favoriteSportsToWatch,
  favoriteVacationSpots,
  fetchMoreCommentLikes,
  fetchMoreComments,
  findingNewJob,
  findingNewJobDesc,
  forumPosts,
  hasMoreComments,
  hasMoreCommentLikes,
  hiring,
  hiringDesc,
  isCurrentUserLead,
  isFetchingMoreCommentLikes,
  isFetchingMoreComments,
  isOwnProfile,
  investingInStartups,
  investingInStartupsDesc,
  name,
  providingServices,
  providingServicesDesc,
  skills,
  topics,
  username,
}: ProfileContentProps) => {
  const NON_WEEKEND_SECTIONS = [
    {
      sectionHeader: 'Topics',
      entities: topics,
      param: 'topics',
      pluralFieldName: 'Topics',
      fieldName: 'Topic',
    },
    {
      sectionHeader: 'Skills',
      entities: skills,
      param: 'skills',
      pluralFieldName: 'Skills',
      fieldName: 'Skill',
    },
  ];
  const WEEKEND_SECTIONS = [
    {
      sectionSubHeader: 'Favorite Vacation Spot',
      entities: favoriteVacationSpots,
      param: 'favorite_vacation_spots',
      pluralFieldName: 'Vacation Spots',
      fieldName: 'Vacation Spot',
    },
    {
      sectionSubHeader: 'Favorite Sports to Play',
      entities: favoriteSportsToPlay,
      param: 'favorite_sport_play',
      pluralFieldName: 'Sports to Play',
      fieldName: 'Sport to Play',
    },
    {
      sectionSubHeader: 'Favorite Sports to Watch',
      entities: favoriteSportsToWatch,
      param: 'favorite_sport_watch',
      pluralFieldName: 'Sports to Watch',
      fieldName: 'Sport to Watch',
    },
    {
      sectionSubHeader: 'Favorite Books',
      entities: favoriteBooks,
      param: 'favorite_book',
      pluralFieldName: 'Favorite Books',
      fieldName: 'Favorite Book',
    },
    {
      sectionSubHeader: 'Favorite TV Shows / Movies',
      entities: favoriteShows,
      param: 'favorite_show',
      pluralFieldName: 'TV Shows / Movies',
      fieldName: 'TV Show / Movie',
    },
  ];

  const OPEN_TO_LIST = [
    {
      openTo: investingInStartups,
      openToParams: 'investing_in_startups',
      openToTitle: 'Looking to invest in startups / private companies',
      openToDesc: investingInStartupsDesc,
    },
    {
      openTo: hiring,
      openToParams: 'hiring',
      openToTitle: 'Looking to hire team members',
      openToDesc: hiringDesc,
    },
    {
      openTo: findingNewJob,
      openToParams: 'finding_new_job',
      openToTitle: 'Looking for a job',
      openToDesc: findingNewJobDesc,
    },
    {
      openTo: providingServices,
      openToParams: 'providing_services',
      openToTitle: 'Looking to provide services',
      openToDesc: providingServicesDesc,
    },
  ];

  const numComments = useMemo(() => {
    const commentsPages = commentsData?.pages;
    return commentsPages ? commentsPages[0]?.comments.length : 0;
  }, [commentsData]);

  const numCommentLikes = useMemo(() => {
    const commentLikes = commentLikesData?.pages;
    return commentLikes ? commentLikes[0]?.comments.length : 0;
  }, [commentLikesData]);

  return (
    <Container className="ProfileContent">
      <Row>
        <Col className="col-sm-12 col-md-2 profile-detail-nav">
          <ul className="profile-nav">
            <li>
              <Link
                className="link-nav"
                activeClass="active"
                spy={true}
                to="About"
                smooth={true}
                offset={-100}
              >
                <div className="profile-nav-icon">
                  <IconAvatar className="w-24" />
                </div>
                About
              </Link>
            </li>
            {(investingInStartups || hiring || findingNewJob || providingServices) && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Open-To"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconOpenTo className="w-16" />
                  </div>
                  Open to
                </Link>
              </li>
            )}
            {!!contributorContent?.length && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Articles"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconArticles className="w-18 h-20" />
                  </div>
                  Articles
                </Link>
              </li>
            )}
            {topics?.length > 0 && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Topics"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconTopic className="w-[26px]" />
                  </div>
                  Topics
                </Link>
              </li>
            )}
            {skills?.length > 0 && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Skills"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconPencil className="w-20" />
                  </div>
                  Skills
                </Link>
              </li>
            )}
            {(favoriteVacationSpots?.length > 0 ||
              favoriteSportsToPlay?.length > 0 ||
              favoriteSportsToWatch?.length > 0 ||
              favoriteBooks?.length > 0 ||
              favoriteShows?.length > 0) && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Weekend"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconWeekend className="w-24" />
                  </div>
                  Weekend
                </Link>
              </li>
            )}
            {forumPosts?.length > 0 && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="ForumPosts"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconForum className="w-20" />
                  </div>
                  Forum posts
                </Link>
              </li>
            )}

            {numComments > 0 && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Comments"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconComment className="w-20" />
                  </div>
                  Comments
                </Link>
              </li>
            )}
            {numCommentLikes > 0 && (
              <li>
                <Link
                  className="link-nav"
                  activeClass="active"
                  to="Likes"
                  spy={true}
                  smooth={true}
                  offset={-100}
                >
                  <div className="profile-nav-icon">
                    <IconLike className="w-20 flex-shrink-0 text-black" />
                  </div>
                  Likes
                </Link>
              </li>
            )}
          </ul>
        </Col>
        <Col className="col-sm-12 col-md-10 profile-detail-wrapper">
          <div className="section-container" id="About">
            <div className="section-header">About</div>
            <div className={`section-detail ${!canViewFullProfile ? 'blur-bio' : ''}`}>{bio}</div>
            {!canViewFullProfile && !isOwnProfile && (
              <GatedContent
                headline={
                  isCurrentUserLead ? 'Join The Information' : 'Join now to view the full profile'
                }
                showTagline={isCurrentUserLead}
                tagline={`${name} and other subscribers to The Information have access to our private directory
                of people serious about technology. Subscribe to access The Information’s
                network along with our exclusive journalism covering the technology industry.`}
              />
            )}
          </div>
          {canViewFullProfile && (
            <>
              {(investingInStartups || hiring || findingNewJob || providingServices) && (
                <div className="section-container" id="Open-To">
                  <div className="section-header">Open to</div>
                  <div className="section-detail">
                    {OPEN_TO_LIST.map(
                      (section, id) =>
                        section.openTo && (
                          <OpenToSection
                            key={id}
                            openToParams={section.openToParams}
                            openToTitle={section.openToTitle}
                            openToDesc={section.openToDesc}
                          />
                        )
                    )}
                  </div>
                </div>
              )}
              {!!contributorContent?.length && (
                <div className="section-container" id="Articles">
                  <div className="section-header">Articles</div>
                  <div className="section-detail">
                    <Feed
                      articlesOrBriefings={contributorContent}
                      paginationData={contributorContentPagination}
                      username={username}
                    />
                  </div>
                </div>
              )}
              {NON_WEEKEND_SECTIONS.map(
                (section, id) =>
                  section.entities.length > 0 && (
                    <div className="section-container" id={section.sectionHeader} key={id}>
                      <div className="section-header">{section.sectionHeader}</div>
                      <Section
                        entities={section.entities}
                        param={section.param}
                        fieldName={section.fieldName}
                        pluralFieldName={section.pluralFieldName}
                        profileName={name}
                      />
                    </div>
                  )
              )}
              {(favoriteVacationSpots.length > 0 ||
                favoriteSportsToPlay.length > 0 ||
                favoriteSportsToWatch.length > 0 ||
                favoriteBooks.length > 0 ||
                favoriteShows.length > 0) && (
                <div className="section-container" id="Weekend">
                  <div className="section-header">Weekend</div>
                  <div className="section-detail">
                    {WEEKEND_SECTIONS.map(
                      (section, id) =>
                        section.entities.length > 0 && (
                          <div className="weekend-container weekend" key={id}>
                            <div className="weekend-header">{section.sectionSubHeader}</div>
                            <Section
                              entities={section.entities}
                              param={section.param}
                              fieldName={section.fieldName}
                              pluralFieldName={section.pluralFieldName}
                              profileName={name}
                            />
                          </div>
                        )
                    )}
                  </div>
                </div>
              )}
              {forumPosts?.length > 0 && (
                <div className="section-container" id="ForumPosts">
                  <div className="section-header">Forum Posts</div>
                  <ForumPostList forumPosts={forumPosts} />
                </div>
              )}
              {numComments > 0 && (
                <div className="section-container" id="Comments">
                  <div className="section-header">Article Comments</div>
                  <div className="section-detail with-comments">
                    {commentsData.pages.map((group) =>
                      group.comments.map((comment) => <Comment {...comment} key={uniqueId()} />)
                    )}
                    {hasMoreComments && (
                      <div className="show-more">
                        {isFetchingMoreComments ? (
                          <Spinner />
                        ) : (
                          <PrimaryButton as="button" onClick={fetchMoreComments}>
                            Show more
                          </PrimaryButton>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {numCommentLikes > 0 && (
                <div className="section-container" id="Likes">
                  <div className="section-header">Article Comment Likes</div>
                  <div className="section-detail with-comments">
                    {commentLikesData.pages.map((group) =>
                      group.comments.map((comment) => <Comment {...comment} key={uniqueId()} />)
                    )}
                    {hasMoreCommentLikes && (
                      <div className="show-more">
                        {isFetchingMoreCommentLikes ? (
                          <Spinner />
                        ) : (
                          <PrimaryButton as="button" onClick={fetchMoreCommentLikes}>
                            Show more
                          </PrimaryButton>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ProfileContent;
